/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\RoleProfileBasicInfoBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Role Profiles module.
----------------------------------------------------------------*/


import { AlertCircle, Users } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';

interface RoleProfileBasicInfoBoxProps {
    name: string;
    description: string;
    isSystem?: boolean;
    debugModeActive: boolean;
    errors: Record<string, string>;
    onNameChange: (v: string) => void;
    onDescriptionChange: (v: string) => void;
    t: (key: string) => string;
}

export function RoleProfileBasicInfoBox({
    name, description, isSystem, debugModeActive, errors,
    onNameChange, onDescriptionChange, t
}: RoleProfileBasicInfoBoxProps) {
    return (
        <FormBox title={t('profiles.sections.basicInfo')} icon={<Users className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                {/* Name */}
                <SimpleTooltip content={t('profiles.tooltips.profileName')}>
                    <div className="flex flex-col">
                        <label className="text-sm font-medium text-foreground">
                            {t('profiles.profileName')} *
                        </label>
                        {debugModeActive && (
                            <span className="text-xs text-muted-foreground mt-0.5">name</span>
                        )}
                    </div>
                </SimpleTooltip>
                <div>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => onNameChange(e.target.value)}
                        disabled={isSystem}
                        className={`w-full px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent ${errors.name ? 'border-red-500' : 'border-border'} ${isSystem ? 'cursor-not-allowed opacity-70 bg-muted/30' : ''}`}
                        placeholder={t('profiles.profileName')}
                    />
                    {errors.name && (
                        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {errors.name}
                        </p>
                    )}
                </div>

                {/* Description */}
                <SimpleTooltip content={t('profiles.tooltips.description')}>
                    <div className="flex flex-col">
                        <label className="text-sm font-medium text-foreground">
                            {t('profiles.description')}
                        </label>
                        {debugModeActive && (
                            <span className="text-xs text-muted-foreground mt-0.5">description</span>
                        )}
                    </div>
                </SimpleTooltip>
                <input
                    type="text"
                    value={description}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                    placeholder={t('profiles.description')}
                />
            </div>
        </FormBox>
    );
}
